/**
 * Role-Based Access Control (RBAC) System
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5, 14.6, 14.7
 * 
 * Defines role hierarchy and permission checking utilities.
 */

import type { User, UserRole } from './auth';

/**
 * Actions that can be performed in the system
 */
export type RBACAction =
  | 'view_content'           // View published content
  | 'edit_draft'             // Edit content in draft state
  | 'submit_for_review'      // Submit content for review
  | 'approve_content'        // Approve or reject content
  | 'publish_content'        // Publish approved content
  | 'manage_company_users'   // Manage users within own company
  | 'manage_company_overlays'// Manage company customizations
  | 'manage_all_companies'   // Access all companies (super admin)
  | 'view_audit_logs'        // View audit logs
  | 'manage_all_courses';    // Full access to all courses

/**
 * Role hierarchy levels (higher number = more permissions)
 * Super_Admin > Company_Admin > Reviewer > Author > Viewer
 * Requirements: 14.1
 */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  viewer: 1,
  author: 2,
  reviewer: 3,
  company_admin: 4,
  super_admin: 5,
} as const;

/**
 * Minimum role required for each action
 * Requirements: 14.2, 14.3, 14.4, 14.5, 14.6
 */
export const ACTION_PERMISSIONS: Record<RBACAction, UserRole> = {
  // Viewer (level 1) - read-only access to published content
  view_content: 'viewer',
  
  // Author (level 2) - create and edit draft content
  edit_draft: 'author',
  submit_for_review: 'author',
  
  // Reviewer (level 3) - approve or reject content
  approve_content: 'reviewer',
  
  // Company Admin (level 4) - manage company resources
  publish_content: 'company_admin',
  manage_company_users: 'company_admin',
  manage_company_overlays: 'company_admin',
  view_audit_logs: 'company_admin',
  
  // Super Admin (level 5) - full system access
  manage_all_companies: 'super_admin',
  manage_all_courses: 'super_admin',
} as const;

/**
 * Check if a role has at least the required permission level
 * Requirements: 14.7
 * 
 * @param userRole - The user's current role
 * @param requiredRole - The minimum role required for the action
 * @returns true if userRole >= requiredRole in the hierarchy
 */
export function hasRoleLevel(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * Check if a user has permission to perform an action
 * Requirements: 14.7
 * 
 * @param user - The user object (or null if not authenticated)
 * @param action - The action to check permission for
 * @returns true if the user has permission, false otherwise
 */
export function hasPermission(user: User | null, action: RBACAction): boolean {
  if (!user) {
    return false;
  }
  
  const requiredRole = ACTION_PERMISSIONS[action];
  return hasRoleLevel(user.role, requiredRole);
}

/**
 * Get all actions a user is permitted to perform
 * 
 * @param user - The user object (or null if not authenticated)
 * @returns Array of permitted actions
 */
export function getPermittedActions(user: User | null): RBACAction[] {
  if (!user) {
    return [];
  }
  
  return (Object.keys(ACTION_PERMISSIONS) as RBACAction[]).filter(
    action => hasPermission(user, action)
  );
}

/**
 * Check if a role is higher than another in the hierarchy
 * 
 * @param role1 - First role to compare
 * @param role2 - Second role to compare
 * @returns true if role1 > role2
 */
export function isRoleHigherThan(role1: UserRole, role2: UserRole): boolean {
  return ROLE_HIERARCHY[role1] > ROLE_HIERARCHY[role2];
}

/**
 * Get the display name for a role
 */
export function getRoleDisplayName(role: UserRole): string {
  const displayNames: Record<UserRole, string> = {
    super_admin: 'Super Admin',
    company_admin: 'Company Admin',
    reviewer: 'Reviewer',
    author: 'Author',
    viewer: 'Viewer',
  };
  return displayNames[role];
}

/**
 * Get all roles that have at least the specified permission level
 * 
 * @param minimumRole - The minimum role level
 * @returns Array of roles at or above the minimum level
 */
export function getRolesWithMinimumLevel(minimumRole: UserRole): UserRole[] {
  const minimumLevel = ROLE_HIERARCHY[minimumRole];
  return (Object.keys(ROLE_HIERARCHY) as UserRole[]).filter(
    role => ROLE_HIERARCHY[role] >= minimumLevel
  );
}
